import { useState, useRef, useEffect } from 'react'
import { DocumentSummary, Folder } from '../../types/models'
import { FolderSelector } from '../folders/FolderSelector'
import StatusBadge from './StatusBadge'

interface DocumentTableProps {
  documents: DocumentSummary[]
  folders: Folder[]
  onBookmark?: (docId: string, bookmarked: boolean) => void
  onClick?: (docId: string) => void
  onSort?: (field: string) => void
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  selectedDocuments?: string[]
  onDocumentSelect?: (documentId: string, isSelected: boolean) => void
  onSelectAll?: (isSelected: boolean) => void
  onUpdateDocument?: (docId: string, updates: { folderId?: string | null }) => void
  onShareChange?: (docId: string, shareType: 'private' | 'organization' | 'specific' | 'public') => void
}

export function DocumentTable({
  documents = [],
  folders,
  onBookmark,
  onClick,
  onSort,
  sortField,
  sortOrder,
  selectedDocuments = [],
  onDocumentSelect,
  onSelectAll,
  onUpdateDocument,
  onShareChange
}: DocumentTableProps) {
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null)
  const [editingField, setEditingField] = useState<'folder' | 'share' | null>(null)
  const [editingFolder, setEditingFolder] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 外部クリックでドロップダウンを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setEditingDocumentId(null)
        setEditingField(null)
        setEditingFolder(null)
      }
    }

    if (editingDocumentId) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editingDocumentId])

  const formatDate = (timestamp: any, short = false) => {
    if (!timestamp) return ''
    
    // 配列の場合
    if (Array.isArray(timestamp)) {
      return '配列データエラー'
    }
    
    let date: Date
    
    // Firestoreタイムスタンプの場合
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate()
    }
    // 秒とナノ秒を持つオブジェクトの場合
    else if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      date = new Date(timestamp.seconds * 1000)
    }
    // _seconds プロパティがある場合（Firebase SDK v9の場合）
    else if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      date = new Date(timestamp._seconds * 1000)
    }
    // ISO文字列の場合
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    }
    // Dateオブジェクトまたは数値の場合
    else {
      date = new Date(timestamp)
    }
    
    // 無効な日付をチェック
    if (isNaN(date.getTime())) {
      return '日付変換エラー'
    }
    
    // コンパクト表記（モバイルはさらに短い「7/10」形式）
    return short
      ? `${date.getMonth() + 1}/${date.getDate()}`
      : `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }

    return sortOrder === 'asc' ? (
      <svg style={{ color: 'var(--color-accent)' }} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ) : (
      <svg style={{ color: 'var(--color-accent)' }} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    )
  }

  const handleStartEdit = (docId: string, field: 'folder', document: DocumentSummary) => {
    setEditingDocumentId(docId)
    setEditingField(field)
    if (field === 'folder') {
      // 現在のフォルダIDを初期値として設定
      const currentFolderId = (document as any).folderId || document.folder?.id || null
      setEditingFolder(currentFolderId)
    }
  }

  // Notionライクな自動保存機能
  const handleFolderSelect = async (docId: string, newFolderId: string | null) => {
    if (!onUpdateDocument) {
      console.error('❌ onUpdateDocument function is not provided!')
      alert('更新機能が利用できません。')
      return
    }

    const updates = { folderId: newFolderId }
    console.log('📁 Auto-saving folder change:', { documentId: docId, folderId: newFolderId })

    try {
      await onUpdateDocument(docId, updates)
      console.log('✅ Folder updated successfully')
      // 編集状態をクリア
      setEditingDocumentId(null)
      setEditingField(null)
      setEditingFolder(null)
    } catch (error) {
      console.error('❌ Folder update failed:', error)
      alert(`フォルダの更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    }
  }

  const handleSaveEdit = async () => {
    if (!editingDocumentId || !editingField) {
      console.log('❌ Save edit failed: Missing required data', { editingDocumentId, editingField })
      return
    }

    const updates: any = {}
    if (editingField === 'folder') {
      updates.folderId = editingFolder
      console.log('📁 Folder update:', { documentId: editingDocumentId, folderId: editingFolder })
    }

    console.log('💾 Calling onUpdateDocument with:', { documentId: editingDocumentId, updates })

    if (!onUpdateDocument) {
      console.error('❌ onUpdateDocument function is not provided!')
      alert('更新機能が利用できません。')
      return
    }

    try {
      console.log('🚀 Starting update process...')
      await onUpdateDocument(editingDocumentId, updates)
      console.log('✅ Update completed successfully')
      // フォルダ更新完了（サイレント保存）
    } catch (error) {
      console.error('❌ Update failed in DocumentTable:', error)
      alert(`更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    }

    setEditingDocumentId(null)
    setEditingField(null)
    setEditingFolder(null)
  }

  const handleCancelEdit = () => {
    setEditingDocumentId(null)
    setEditingField(null)
    setEditingFolder(null)
  }

  return (
    <div className="table-container">
      <table className="documents-table">
        <thead>
          <tr>
            {onSelectAll && (
              <th scope="col" className="td-checkbox hide-mobile">
                <input
                  type="checkbox"
                  checked={documents.length > 0 && selectedDocuments.length === documents.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th>
            )}
            <th
              scope="col"
              className="sortable"
              onClick={() => onSort?.('createdAt')}
            >
              <div className="flex items-center gap-1">
                作成日時
                <SortIcon field="createdAt" />
              </div>
            </th>
            <th
              scope="col"
              className="sortable"
              onClick={() => onSort?.('title')}
            >
              <div className="flex items-center gap-1">
                タイトル
                <SortIcon field="title" />
              </div>
            </th>
            <th scope="col" className="hide-mobile">
              フォルダ
            </th>
            <th scope="col" className="hide-mobile">
              ステータス
            </th>
            <th scope="col" className="hide-mobile">
              <span className="sr-only">ブックマーク</span>
            </th>
            <th scope="col" className="hide-mobile">
              共有
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.filter(document => document && document.id).map((document) => (
            <tr key={document.id}>
              {onDocumentSelect && (
                <td className="td-checkbox hide-mobile">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.includes(document.id)}
                    onChange={(e) => {
                      e.stopPropagation()
                      onDocumentSelect(document.id, e.target.checked)
                    }}
                  />
                </td>
              )}
              <td
                className="whitespace-nowrap cursor-pointer"
                onClick={() => onClick?.(document.id)}
              >
                <span className="hide-mobile">{formatDate(document.createdAt)}</span>
                <span className="mobile-only">{formatDate(document.createdAt, true)}</span>
              </td>
              <td
                className="whitespace-nowrap cursor-pointer"
                onClick={() => onClick?.(document.id)}
              >
                <div className="flex flex-col">
                  <div className="td-primary truncate max-w-xs">
                    {document.title}
                    {document.hasRecording && (
                      <span title="録画あり" style={{ marginLeft: 6 }}>🎥</span>
                    )}
                    {document.visibility && (
                      <span
                        title={
                          document.visibility === 'private' ? '公開範囲: 自分だけ'
                            : document.visibility === 'members' ? '公開範囲: 指定メンバー'
                            : document.visibility === 'folder' ? '公開範囲: フォルダ内'
                            : '公開範囲: 組織全体'
                        }
                        style={{ marginLeft: 6, fontSize: 12, opacity: 0.8 }}
                      >
                        {document.visibility === 'private' ? '🔒'
                          : document.visibility === 'members' ? '👥'
                          : document.visibility === 'folder' ? '📁'
                          : '🏢'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-secondary truncate max-w-xs">
                    {document.excerpt}
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap relative hide-mobile">
                {editingDocumentId === document.id && editingField === 'folder' ? (
                  <div ref={dropdownRef} className="relative">
                    {/* Notionライクなドロップダウン */}
                    <div className="dropdown-menu open max-h-64 overflow-y-auto" style={{ top: 0 }}>
                      {/* フォルダなしオプション */}
                      <div
                        className="dropdown-item"
                        onClick={() => handleFolderSelect(document.id, null)}
                      >
                        <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-border-subtle)' }} />
                        <span>フォルダなし</span>
                      </div>

                      {/* フォルダオプション */}
                      {folders.map((folder) => (
                        <div
                          key={folder.id}
                          className="dropdown-item"
                          onClick={() => handleFolderSelect(document.id, folder.id)}
                        >
                          <div
                            className="w-3 h-3 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: folder.color || '#3B82F6' }}
                          />
                          <span className="truncate">
                            {folder.name}
                          </span>
                          <span className="text-xs text-tertiary ml-auto">
                            {folder.documentCount}件
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    className="cursor-pointer p-1 rounded group transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleStartEdit(document.id, 'folder', document)
                    }}
                  >
                    {(() => {
                      // 一覧のフォルダデータ（color付き）を優先し、なければドキュメント内のfolderを使用
                      const targetFolderId = (document as any).folderId || document.folder?.id;
                      const folderFromList = folders.find(f => f.id === targetFolderId);
                      const folder = folderFromList || document.folder;
                      if (folder) {
                        return (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0"
                              style={{ backgroundColor: folder.color || '#3B82F6' }}
                            />
                            <span className="text-sm text-primary truncate max-w-32">
                              {folder.name}
                            </span>
                            <svg className="w-3 h-3 row-kebab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        );
                      } else {
                        return (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-border-subtle)' }} />
                            <span className="text-sm text-tertiary">フォルダなし</span>
                            <svg className="w-3 h-3 row-kebab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}
              </td>
              <td
                className="px-6 py-4 whitespace-nowrap cursor-pointer hide-mobile"
                onClick={() => onClick?.(document.id)}
              >
                <StatusBadge status={document?.status} botStatus={(document as any)?.botStatus} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium hide-mobile">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    console.log('🔖 Bookmark click:', {
                      docId: document.id,
                      currentBookmarked: document.bookmarked,
                      willToggleTo: !document.bookmarked,
                      fullDocument: document
                    })
                    onBookmark?.(document.id, !document.bookmarked)
                  }}
                  className="btn-icon borderless"
                  style={document.bookmarked ? { color: 'var(--color-warning-mid)' } : undefined}
                  title={document.bookmarked ? 'ブックマークを解除' : 'ブックマークに追加'}
                >
                  <svg className="w-5 h-5" fill={document.bookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap relative hide-mobile">
                {editingDocumentId === document.id && editingField === 'share' ? (
                  <div ref={dropdownRef} className="relative">
                    {/* 共有設定ドロップダウン */}
                    <div className="dropdown-menu open" style={{ top: 0 }}>
                      {/* 非公開 */}
                      <div
                        className="dropdown-item"
                        onClick={() => {
                          onShareChange?.(document.id, 'private')
                          setEditingDocumentId(null)
                          setEditingField(null)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>非公開</span>
                      </div>
                      
                      {/* 組織内全員 */}
                      <div
                        className="dropdown-item"
                        onClick={() => {
                          onShareChange?.(document.id, 'organization')
                          setEditingDocumentId(null)
                          setEditingField(null)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>組織内全員</span>
                      </div>
                      
                      {/* 特定のユーザー */}
                      <div
                        className="dropdown-item"
                        onClick={() => {
                          onShareChange?.(document.id, 'specific')
                          setEditingDocumentId(null)
                          setEditingField(null)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>特定のユーザー</span>
                      </div>
                      
                      {/* 公開 */}
                      <div
                        className="dropdown-item"
                        onClick={() => {
                          onShareChange?.(document.id, 'public')
                          setEditingDocumentId(null)
                          setEditingField(null)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>URLを知っている人</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="cursor-pointer p-1 rounded group transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      setEditingDocumentId(document.id)
                      setEditingField('share')
                    }}
                  >
                    {(document as any).shareInfo ? (
                      <div className="flex items-center gap-2">
                        {/* 共有タイプアイコン */}
                        {(document as any).shareInfo.type === 'public' && (
                          <div className="flex items-center gap-1 text-secondary" title="URLを知っている人">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs">公開</span>
                          </div>
                        )}
                        {(document as any).shareInfo.type === 'organization' && (
                          <div className="flex items-center gap-1 text-secondary" title="組織内全員">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-xs">組織</span>
                          </div>
                        )}
                        {(document as any).shareInfo.type === 'specific' && (
                          <div className="flex items-center gap-1 text-secondary" title="特定のユーザー">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="text-xs">特定</span>
                          </div>
                        )}
                        <svg className="w-3 h-3 row-kebab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    ) : (
                      // 共有リンクなし: 公開範囲（visibility）を表示（「非公開」はvisibilityがprivateの場合のみ）
                      (() => {
                        const vis = (document as any).visibility as string | undefined
                        const conf = vis === 'org'
                          ? { label: '組織全体', title: '組織のメンバー全員が閲覧できます', icon: 'org' }
                          : vis === 'folder'
                            ? { label: 'フォルダ内', title: 'フォルダにアクセスできるメンバーが閲覧できます', icon: 'org' }
                            : vis === 'members'
                              ? { label: 'メンバー', title: '指定したメンバーが閲覧できます', icon: 'user' }
                              : { label: '非公開', title: '自分だけが閲覧できます', icon: 'lock' }
                        return (
                          <div className="flex items-center gap-2 text-tertiary" title={conf.title}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {conf.icon === 'lock' && (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              )}
                              {conf.icon === 'user' && (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              )}
                              {conf.icon === 'org' && (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              )}
                            </svg>
                            <span className="text-xs">{conf.label}</span>
                            <svg className="w-3 h-3 row-kebab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        )
                      })()
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {documents.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="empty-state-title">議事録がありません</h3>
          <p className="empty-state-desc">
            新しい議事録を作成して始めましょう
          </p>
        </div>
      )}
    </div>
  )
}